// @flow

/**
 * Convert data display definitions to markdown for PDF/DOCX export.
 * Tables become markdown tables. Graphs become markdown tables with a type annotation
 * (used as fallback when chart image capture fails).
 */

import { formatValueForExport } from '../components/data-display/shared/formatters';

/**
 * Derive a human-readable chart type label from a display definition.
 */
function deriveChartLabel(definition: Object): string {
  const types = new Set((definition.yAxes || []).map(y => y.seriesType || 'line'));
  if (types.size === 1) {
    return types.has('bar') ? 'Bar Graph' : 'Line Graph';
  }
  return 'Composed Chart';
}

/**
 * Convert a table display definition to a markdown table.
 */
export function convertTableToMarkdown(definition: Object): string {
  const { title, columns = [], rows = [] } = definition;
  const header = `**${title}**\n\n`;
  const headerRow = `| ${columns.map(c => c.header).join(' | ')} |`;
  const separator = `| ${columns.map(() => '---').join(' | ')} |`;
  const dataRows = rows
    .map(
      row =>
        `| ${columns.map(c => formatValueForExport(row[c.key], c.valueType)).join(' | ')} |`,
    )
    .join('\n');

  return `${header}${headerRow}\n${separator}\n${dataRows}`;
}

/**
 * Convert a graph display definition to a markdown table (fallback when image capture fails).
 */
export function convertGraphToMarkdown(definition: Object): string {
  const { title, xAxisKey, xAxisLabel, yAxes = [], data = [] } = definition;
  const chartTypeLabel = deriveChartLabel(definition);
  const header = `**${title}** *(${chartTypeLabel})*\n\n`;

  const columns = [
    { key: xAxisKey, header: xAxisLabel || xAxisKey, valueType: 'string' },
    ...yAxes.map(y => ({ key: y.key, header: y.label, valueType: y.valueType })),
  ];

  const headerRow = `| ${columns.map(c => c.header).join(' | ')} |`;
  const separator = `| ${columns.map(() => '---').join(' | ')} |`;
  const dataRows = data
    .map(
      point =>
        `| ${columns.map(c => formatValueForExport(point[c.key], c.valueType)).join(' | ')} |`,
    )
    .join('\n');

  return `${header}${headerRow}\n${separator}\n${dataRows}`;
}

/**
 * Convert any display definition to markdown.
 */
export function convertDisplayToMarkdown(definition: Object): string {
  if (!definition || !definition.displayType) {
    return '*(Visual component data unavailable)*';
  }

  switch (definition.displayType) {
    case 'table':
      return convertTableToMarkdown(definition);
    case 'composedGraph':
      return convertGraphToMarkdown(definition);
    default:
      return `*(Unsupported display type: ${definition.displayType})*`;
  }
}
